import { keyPathMirror } from 'key-path-mirror';

import { LocalesKey } from '../localesKeysTypes';

interface TwinsKeyType {
  plural: LocalesKey;
  singular: LocalesKey;
  create: LocalesKey;
  code: LocalesKey;
  name: LocalesKey;
  integrationCodes: {
    title: LocalesKey;
    hint: LocalesKey;
    addRow: LocalesKey;
  };
  status: {
    draft: LocalesKey;
    published: LocalesKey;
    deactivated: LocalesKey;
  };
  searchPlaceholder: LocalesKey;
  workspace: {
    publish: LocalesKey;
    comingSoon: LocalesKey;
    tabs: {
      variants: LocalesKey;
      parts: LocalesKey;
      materials: LocalesKey;
      rules: LocalesKey;
      preview: LocalesKey;
    };
  };
  simple: {
    steps: {
      model: LocalesKey;
      parts: LocalesKey;
      materials: LocalesKey;
    };
    notConfigured: LocalesKey;
    title: LocalesKey;
  };
  variants: {
    plural: LocalesKey;
    addVariant: LocalesKey;
    createTitle: LocalesKey;
    editTitle: LocalesKey;
    deleteTitle: LocalesKey;
    deleteWarning: LocalesKey;
    defaultLabel: LocalesKey;
    setDefault: LocalesKey;
    product: LocalesKey;
    baseModel: LocalesKey;
    noVariants: LocalesKey;
    changeBaseModel: LocalesKey;
    noBaseModel: LocalesKey;
  };
  parts: {
    createTitle: LocalesKey;
    addPart: LocalesKey;
    save: LocalesKey;
    remove: LocalesKey;
    glbAsset: LocalesKey;
    noVariants: LocalesKey;
    noBaseModel: LocalesKey;
    noSockets: LocalesKey;
    noParts: LocalesKey;
    noConnector: LocalesKey;
    compatible: LocalesKey;
    existingGlbAsset: LocalesKey;
    uploadNewGlbAsset: LocalesKey;
    newGlbAssetName: LocalesKey;
  };
  library: {
    searchPlaceholder: LocalesKey;
    empty: LocalesKey;
    noGlb: LocalesKey;
    noProduct: LocalesKey;
  };
  socketPanel: {
    title: LocalesKey;
    modeLabel: LocalesKey;
    acceptByRule: LocalesKey;
    curatedOnly: LocalesKey;
    candidates: LocalesKey;
    noCandidates: LocalesKey;
    candidatesHint: LocalesKey;
    patternAffects: LocalesKey;
    prefixOverMatchWarning: LocalesKey;
    socketsTitle: LocalesKey;
    searchPlaceholder: LocalesKey;
    selectSocket: LocalesKey;
    assignedTitle: LocalesKey;
    assignedHint: LocalesKey;
    inheritedTitle: LocalesKey;
    inheritedHint: LocalesKey;
    modeHintAcceptByRule: LocalesKey;
    modeHintCuratedOnly: LocalesKey;
    modeSavesImmediately: LocalesKey;
    nestedOn: LocalesKey;
    emptySocket: LocalesKey;
    configuredCount: LocalesKey;
  };
  sections: {
    base: LocalesKey;
    slots: LocalesKey;
    materials: LocalesKey;
    rules: LocalesKey;
    preview: LocalesKey;
    copyId: LocalesKey;
    copied: LocalesKey;
  };
  wizard: {
    title: LocalesKey;
    stepBase: LocalesKey;
    next: LocalesKey;
    back: LocalesKey;
    finish: LocalesKey;
    createAndContinue: LocalesKey;
    fromLibrary: LocalesKey;
    uploadNew: LocalesKey;
    basisRequired: LocalesKey;
    productRequired: LocalesKey;
    companyRequired: LocalesKey;
    nameRequired: LocalesKey;
    codeRequired: LocalesKey;
  };
  crm: {
    sectionTitle: LocalesKey;
    rolePart: LocalesKey;
    roleVariant: LocalesKey;
    usedInConfigurators: LocalesKey;
    notUsed: LocalesKey;
    usageMayBeIncomplete: LocalesKey;
    listMayBeIncomplete: LocalesKey;
    empty: LocalesKey;
    materialsEmpty: LocalesKey;
    addModel: LocalesKey;
    addTwinMaterial: LocalesKey;
    roleQuestion: LocalesKey;
    companyLabel: LocalesKey;
    configuratorLabel: LocalesKey;
    createNewConfigurator: LocalesKey;
  };
}

export const twinsKeys = keyPathMirror<TwinsKeyType, string>(
  {
    plural: null,
    singular: null,
    create: null,
    code: null,
    name: null,
    integrationCodes: {
      title: null,
      hint: null,
      addRow: null
    },
    status: {
      draft: null,
      published: null,
      deactivated: null
    },
    searchPlaceholder: null,
    workspace: {
      publish: null,
      comingSoon: null,
      tabs: {
        variants: null,
        parts: null,
        materials: null,
        rules: null,
        preview: null
      }
    },
    simple: {
      steps: {
        model: null,
        parts: null,
        materials: null
      },
      notConfigured: null,
      title: null
    },
    variants: {
      plural: null,
      addVariant: null,
      createTitle: null,
      editTitle: null,
      deleteTitle: null,
      deleteWarning: null,
      defaultLabel: null,
      setDefault: null,
      product: null,
      baseModel: null,
      noVariants: null,
      changeBaseModel: null,
      noBaseModel: null
    },
    parts: {
      createTitle: null,
      addPart: null,
      save: null,
      remove: null,
      glbAsset: null,
      noVariants: null,
      noBaseModel: null,
      noSockets: null,
      noParts: null,
      noConnector: null,
      compatible: null,
      existingGlbAsset: null,
      uploadNewGlbAsset: null,
      newGlbAssetName: null
    },
    library: {
      searchPlaceholder: null,
      empty: null,
      noGlb: null,
      noProduct: null
    },
    socketPanel: {
      title: null,
      modeLabel: null,
      acceptByRule: null,
      curatedOnly: null,
      candidates: null,
      noCandidates: null,
      candidatesHint: null,
      patternAffects: null,
      prefixOverMatchWarning: null,
      socketsTitle: null,
      searchPlaceholder: null,
      selectSocket: null,
      assignedTitle: null,
      assignedHint: null,
      inheritedTitle: null,
      inheritedHint: null,
      modeHintAcceptByRule: null,
      modeHintCuratedOnly: null,
      modeSavesImmediately: null,
      nestedOn: null,
      emptySocket: null,
      configuredCount: null
    },
    sections: {
      base: null,
      slots: null,
      materials: null,
      rules: null,
      preview: null,
      copyId: null,
      copied: null
    },
    wizard: {
      title: null,
      stepBase: null,
      next: null,
      back: null,
      finish: null,
      createAndContinue: null,
      fromLibrary: null,
      uploadNew: null,
      basisRequired: null,
      productRequired: null,
      companyRequired: null,
      nameRequired: null,
      codeRequired: null
    },
    crm: {
      sectionTitle: null,
      rolePart: null,
      roleVariant: null,
      usedInConfigurators: null,
      notUsed: null,
      usageMayBeIncomplete: null,
      listMayBeIncomplete: null,
      empty: null,
      materialsEmpty: null,
      addModel: null,
      addTwinMaterial: null,
      roleQuestion: null,
      companyLabel: null,
      configuratorLabel: null,
      createNewConfigurator: null
    }
  },
  'models.twins.'
);
